/**
 * HomeLauncher — the destinations of the nav rail as large tiles over the empty stage, one for
 * each tab of a panel that has tabs. Each tile tells what its place is for and, where it is
 * useful, what the place holds now. PanelStage mounts the launcher only while no panel is open.
 */

import { useAtomValueRawSync } from 'jotai';
import type { ReactElement } from 'react';
import React, { useRef } from 'react';

import { useCameras } from '@/hooks/useCameras';
import { useDoUpdatesNeedAttention } from '@/hooks/useDoUpdatesNeedAttention';
import { useElementSize } from '@/hooks/useElementSize';
import { useHealthIssues } from '@/hooks/useHealthIssues';
import { useMemberPermissions } from '@/hooks/useMemberPermissions';
import { useServers } from '@/hooks/useServers';
import { offlineCameraCountAtom, recentActivityAtom } from '@/state/apiReads';
import { openIdsAtom, visibleIdsAtom } from '@/state/ui/shellPanelsAtoms';
import { ElementBox } from '@/types/layout';
import type { LauncherNumbers, OpenPanelFn } from '@/types/panels';

import { countBySeverity, getLauncherInfo } from './launcherInfo';
import { layoutLauncher, TILE_GAP_PX } from './launcherLayout';
import { LauncherTile } from './LauncherTile';
import { listLauncherPlaces, openLauncherPlace } from './navEntries';
import { NAV_PAD } from './navResize';

interface Props {
    openPanel: OpenPanelFn;
}

export const HomeLauncher = ({ openPanel }: Props): ReactElement => {
    const visibleIds = useAtomValueRawSync(visibleIdsAtom);
    const openIds = useAtomValueRawSync(openIdsAtom);
    const { canManage } = useMemberPermissions();
    const { cameras } = useCameras();
    // The listed row says nothing about reachability: only the stream's status frames do.
    const offlineCameras = useAtomValueRawSync(offlineCameraCountAtom);
    const { count: serverCount, onlineCount } = useServers();
    const { issues, isStale: isHealthStale } = useHealthIssues();
    const recentActivitySnapshot = useAtomValueRawSync(recentActivityAtom);
    const doUpdatesNeedAttention = useDoUpdatesNeedAttention();
    const boxRef = useRef<HTMLDivElement>(null);
    const box = useElementSize(boxRef, { box: ElementBox.Border });

    const places = listLauncherPlaces(canManage);
    const byKey = new Map(places.map((place) => [place.key, place]));
    const grid = layoutLauncher(box, places.map((place) => place.key));
    const numbers: LauncherNumbers = {
        cameras: { total: cameras.length, offline: offlineCameras },
        servers: { total: serverCount, offline: serverCount - onlineCount },
        health: countBySeverity(issues),
        isHealthStale,
        recentActivity: recentActivitySnapshot,
    };

    return (
        <div
            // The rail's own padding on all four sides: the tiles are the stage's answer to its
            // buttons, and they stand off the window edges by the same 16px. The bottom clears
            // the phone's home indicator, as a panel's surface does.
            className="absolute inset-0"
            style={{
                padding: NAV_PAD,
                paddingBottom: `calc(${NAV_PAD}px + env(safe-area-inset-bottom, 0px))`,
            }}
        >
            <div
                ref={boxRef}
                className="grid size-full"
                style={{
                    gap: TILE_GAP_PX,
                    gridTemplateColumns: `repeat(${grid.columns}, minmax(0, 1fr))`,
                    gridTemplateRows: `repeat(${grid.rows}, minmax(0, 1fr))`,
                }}
            >
                {grid.slots.map((slot) => {
                    const place = byKey.get(slot.id);
                    return place === undefined ? null : (
                        <LauncherTile
                            key={slot.id}
                            place={place}
                            slot={slot}
                            info={getLauncherInfo(slot.id, numbers)}
                            hasDot={place.entryId === 'servers' && doUpdatesNeedAttention}
                            onClick={(): void => openLauncherPlace(
                                place, { visibleIds, openIds }, openPanel)}
                        />
                    );
                })}
            </div>
        </div>
    );
};
